import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { openModal, setLotSize } from '../../store/uiSlice';
import { fmtP } from '../../utils/formatters';
import { LOT_LABELS, getLotSize } from '../../utils/constants';
import { getMarketStatus, getMarketStatusBadge } from '../../utils/marketHours';
import { calcForexPnl, getNotionalUSD } from '../../utils/forexUtils';

const getSpread = (price, category) => {
  if (!price || price === 0) return 0;
  if (category === 'forex') return 0.0002;
  if (category === 'crypto') return price * 0.001;
  if (category === 'metal') return price * 0.002;
  return price * 0.002;
};

const getLeverageMultiplier = (leverageStr, category) => {
  if (category === 'crypto') return 25;
  if (category === 'stock') return 4;
  if (!leverageStr) return 50;
  const parts = String(leverageStr).split(':');
  return parseInt(parts[1]) || parseInt(parts[0]) || 50;
};

const getLeverageDisplay = (category, userLeverage) => {
  if (category === 'crypto') return '1:25';
  if (category === 'stock') return '1:4';
  return userLeverage || '1:100';
};

const OrderPanel = ({ instrument, isMobile = false }) => {
  const dispatch = useDispatch();
  const { lotSize } = useSelector((state) => state.ui);
  const { marketData, balance } = useSelector((state) => state.trade);
  const user = useSelector((state) => state.auth.user);
  const theme = useSelector((state) => state.ui.theme);
  const isLight = theme === 'light';
  const [marketStatus, setMarketStatus] = useState({ open: true, reason: '' });
  const [manualLotInput, setManualLotInput] = useState(String(lotSize || 0.01));

  const userLeverage = user?.leverage || '1:100';
  const leverageMultiplier = getLeverageMultiplier(userLeverage, instrument?.cat);
  const leverageDisplay = getLeverageDisplay(instrument?.cat, userLeverage);

  useEffect(() => { if (instrument) setMarketStatus(getMarketStatus(instrument.id, instrument.cat)); }, [instrument]);
  useEffect(() => { setManualLotInput(String(lotSize)); }, [lotSize]);

  const handleLotChange = (delta) => {
    let newLot = parseFloat(manualLotInput) + delta;
    if (isNaN(newLot)) newLot = 0.01; if (newLot < 0.01) newLot = 0.01; if (newLot > 100) newLot = 100;
    newLot = parseFloat(newLot.toFixed(2));
    dispatch(setLotSize(newLot)); setManualLotInput(String(newLot));
  };

  const handleLotInput = (e) => { const raw = e.target.value; setManualLotInput(raw); let val = parseFloat(raw); if (isNaN(val) || val < 0.01) return; if (val > 100) val = 100; dispatch(setLotSize(parseFloat(val.toFixed(2)))); };
  const handleLotBlur = () => { let val = parseFloat(manualLotInput); if (isNaN(val) || val < 0.01) val = 0.01; if (val > 100) val = 100; val = parseFloat(val.toFixed(2)); dispatch(setLotSize(val)); setManualLotInput(String(val)); };

  if (!instrument) {
    return (
      <div className="flex flex-col items-center justify-center h-full w-full p-4 text-center">
        <div className="w-14 h-14 rounded-xl flex items-center justify-center text-2xl mb-3" style={{ background: 'var(--color-orangeBg)', border: '1px solid var(--color-orangeBorder)', color: 'var(--color-text)' }}><i className="fas fa-bolt" /></div>
        <p className="text-sm font-syne font-bold" style={{ color: 'var(--color-textSecondary)' }}>Select an instrument</p>
        <p className="text-xs font-mono mt-1" style={{ color: 'var(--color-textSecondary)' }}>to start trading</p>
      </div>
    );
  }

  const data = marketData[instrument.id];
  const price = data?.price || 0;
  const spread = getSpread(price, instrument.cat);
  const bidPrice = price - spread / 2;
  const askPrice = price + spread / 2;
  const statusBadge = getMarketStatusBadge(instrument.id, instrument.cat);
  const lotSizeValue = getLotSize(instrument.cat);
  const totalCost = getNotionalUSD(instrument.id, askPrice, lotSize, instrument.cat, lotSizeValue, marketData);
  const requiredMargin = totalCost / leverageMultiplier;
  const marginPercent = balance > 0 ? (requiredMargin / balance * 100) : 0;
  const estimatedProfitBuy = calcForexPnl(false, askPrice * 1.005, askPrice, lotSize, instrument.id, instrument.cat, lotSizeValue, marketData);
  const estimatedProfitSell = calcForexPnl(true, bidPrice * 0.995, bidPrice, lotSize, instrument.id, instrument.cat, lotSizeValue, marketData);

  const btnMinH = isMobile ? 'min-h-[52px]' : 'min-h-[44px]';
  const textSize = isMobile ? 'text-sm' : 'text-[13px]';

  // Theme-aware styles for top tabs
  const sellTabBg = isLight ? '#dc2626' : 'rgba(220, 38, 38, 0.15)';
  const sellTabBorder = isLight ? 'none' : '1px solid rgba(220, 38, 38, 0.3)';
  const sellTabShadow = isLight ? '0 4px 16px rgba(220,38,38,0.3)' : 'none';
  const sellLabelColor = isLight ? '#ffffff' : '#f87171';
  const sellPriceColor = isLight ? '#ffffff' : '#fca5a5';
  const sellEstColor = isLight ? 'rgba(255,255,255,0.8)' : 'rgba(248,113,113,0.7)';

  const buyTabBg = isLight ? '#059669' : 'rgba(5, 150, 105, 0.15)';
  const buyTabBorder = isLight ? 'none' : '1px solid rgba(5, 150, 105, 0.3)';
  const buyTabShadow = isLight ? '0 4px 16px rgba(5,150,105,0.3)' : 'none';
  const buyLabelColor = isLight ? '#ffffff' : '#34d399';
  const buyPriceColor = isLight ? '#ffffff' : '#6ee7b7';
  const buyEstColor = isLight ? 'rgba(255,255,255,0.8)' : 'rgba(52,211,153,0.7)';

  // Theme-aware styles for bottom action buttons
  const sellBtnBg = isLight ? 'linear-gradient(to right, #dc2626, #b91c1c)' : 'rgba(220, 38, 38, 0.2)';
  const sellBtnBorder = isLight ? 'none' : '1px solid rgba(220, 38, 38, 0.35)';
  const sellBtnColor = '#ffffff';
  const sellBtnHover = isLight ? 'brightness(1.1)' : 'brightness(1.3)';

  const buyBtnBg = isLight ? 'linear-gradient(to right, #059669, #047857)' : 'rgba(5, 150, 105, 0.2)';
  const buyBtnBorder = isLight ? 'none' : '1px solid rgba(5, 150, 105, 0.35)';
  const buyBtnColor = '#ffffff';
  const buyBtnHover = isLight ? 'brightness(1.1)' : 'brightness(1.3)';

  return (
    <div className="flex flex-col h-full w-full">
      <div className="px-4 py-3 border-b" style={{ background: 'linear-gradient(to right, var(--color-orangeBg), var(--color-purpleBg))', borderColor: 'var(--color-borderMedium)' }}>
        <div className="font-syne text-base font-extrabold" style={{ color: 'var(--color-text)' }}>{instrument.id}</div>
        <div className="text-xs font-mono font-bold mt-0.5" style={{ color: 'var(--color-textSecondary)' }}>{instrument.name}</div>
        <div className="flex items-center gap-2 mt-2">
          <div className="flex items-center gap-1.5 text-[10px] font-mono font-bold px-2 py-0.5 rounded" style={{ background: statusBadge.bg, color: statusBadge.color, border: `1px solid ${statusBadge.border}` }}>
            <span className={`w-2 h-2 rounded-full ${marketStatus.open ? 'animate-pulse' : ''}`} style={{ background: marketStatus.open ? 'var(--color-green)' : 'var(--color-red)' }} />{statusBadge.label}
          </div>
        </div>
      </div>

      {/* Theme-aware Buy/Sell Price Tabs */}
      <div className="grid grid-cols-2 gap-3 p-3">
        <button onClick={() => marketStatus.open && dispatch(openModal('sellModal'))} disabled={!marketStatus.open}
          className="flex flex-col items-center justify-center rounded-xl p-4 text-center transition-all active:scale-95 disabled:opacity-40"
          style={{ 
            background: sellTabBg, 
            border: sellTabBorder, 
            minHeight: isMobile ? '90px' : 'auto', 
            boxShadow: sellTabShadow
          }}
          onMouseEnter={e => { if (!isLight) e.currentTarget.style.background = 'rgba(220, 38, 38, 0.25)'; }}
          onMouseLeave={e => { if (!isLight) e.currentTarget.style.background = 'rgba(220, 38, 38, 0.15)'; }}>
          <div className="text-[10px] font-extrabold tracking-[0.1em] uppercase" style={{ color: sellLabelColor }}>SELL / SHORT</div>
          <div className="text-base font-syne font-extrabold mt-1.5" style={{ color: sellPriceColor }}>{fmtP(bidPrice, instrument.cat)}</div>
          <div className="text-[10px] font-mono font-bold mt-1" style={{ color: sellEstColor }}>Est: ${estimatedProfitSell.toFixed(2)}</div>
        </button>
        <button onClick={() => marketStatus.open && dispatch(openModal('buyModal'))} disabled={!marketStatus.open}
          className="flex flex-col items-center justify-center rounded-xl p-4 text-center transition-all active:scale-95 disabled:opacity-40"
          style={{ 
            background: buyTabBg, 
            border: buyTabBorder, 
            minHeight: isMobile ? '90px' : 'auto', 
            boxShadow: buyTabShadow 
          }}
          onMouseEnter={e => { if (!isLight) e.currentTarget.style.background = 'rgba(5, 150, 105, 0.25)'; }}
          onMouseLeave={e => { if (!isLight) e.currentTarget.style.background = 'rgba(5, 150, 105, 0.15)'; }}>
          <div className="text-[10px] font-extrabold tracking-[0.1em] uppercase" style={{ color: buyLabelColor }}>BUY / LONG</div>
          <div className="text-base font-syne font-extrabold mt-1.5" style={{ color: buyPriceColor }}>{fmtP(askPrice, instrument.cat)}</div>
          <div className="text-[10px] font-mono font-bold mt-1" style={{ color: buyEstColor }}>Est: ${estimatedProfitBuy.toFixed(2)}</div>
        </button>
      </div>

      <div className="px-4 py-3 border-t" style={{ borderColor: 'var(--color-borderMedium)' }}>
        <div className="text-[10px] font-mono font-bold tracking-[0.08em] uppercase mb-2 flex items-center gap-1.5" style={{ color: 'var(--color-textSecondary)' }}><i className="fas fa-layer-group text-[10px]" style={{ color: 'var(--color-text)' }} /> Lot Size</div>
        <div className="flex items-center rounded-lg overflow-hidden" style={{ border: '1px solid var(--color-borderStrong)' }}>
          <button onClick={() => handleLotChange(-0.01)} className="w-10 h-10 cursor-pointer text-lg font-bold flex items-center justify-center active:scale-90 transition-all" style={{ background: 'var(--color-bgSurfaceLight)', border: 'none', color: 'var(--color-text)' }}>−</button>
          <input type="text" inputMode="decimal" value={manualLotInput} onChange={handleLotInput} onBlur={handleLotBlur}
            className="flex-1 text-center font-syne text-base font-bold py-2.5 outline-none border-x" style={{ background: 'var(--color-bgInput)', borderColor: 'var(--color-borderStrong)', color: 'var(--color-text)' }} />
          <button onClick={() => handleLotChange(0.01)} className="w-10 h-10 cursor-pointer text-lg font-bold flex items-center justify-center active:scale-90 transition-all" style={{ background: 'var(--color-bgSurfaceLight)', border: 'none', color: 'var(--color-text)' }}>+</button>
        </div>
        <div className="text-[9px] font-mono font-bold mt-2" style={{ color: 'var(--color-textSecondary)' }}>{LOT_LABELS[instrument.cat]}</div>
      </div>

      <div className="mt-auto p-4 border-t" style={{ background: 'var(--color-bgCard)', borderColor: 'var(--color-borderMedium)' }}>
        {[
          { label: 'Total Cost', value: `$${totalCost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, color: 'var(--color-text)' },
          { label: 'Required Margin', value: `$${requiredMargin.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, color: 'var(--color-blue)' },
          { label: 'Leverage', value: leverageDisplay, color: 'var(--color-purpleLight)' },
          { label: '% of Balance', value: `${marginPercent.toFixed(1)}%`, color: marginPercent < 10 ? 'var(--color-green)' : marginPercent < 25 ? 'var(--color-yellow)' : marginPercent < 50 ? 'var(--color-text)' : 'var(--color-red)' },
        ].map((row, i) => (
          <div key={i} className="flex justify-between items-center py-2">
            <span className="text-[11px] font-mono font-bold" style={{ color: 'var(--color-textSecondary)' }}>{row.label}</span>
            <span className="font-mono font-extrabold text-sm" style={{ color: row.color }}>{row.value}</span>
          </div>
        ))}
      </div>

      {/* Theme-aware Bottom Action Buttons */}
      <div className="grid grid-cols-2 gap-3 p-3 pt-0">
        <button onClick={() => marketStatus.open && dispatch(openModal('sellModal'))} disabled={!marketStatus.open}
          className={`flex items-center justify-center gap-2 py-3.5 rounded-lg font-syne font-bold ${textSize} transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed ${btnMinH}`}
          style={{ 
            background: sellBtnBg, 
            border: sellBtnBorder,
            color: sellBtnColor 
          }}
          onMouseEnter={e => { e.currentTarget.style.filter = sellBtnHover; }}
          onMouseLeave={e => { e.currentTarget.style.filter = 'none'; }}>
          <i className="fas fa-arrow-trend-down text-xs" /> SELL
        </button>
        <button onClick={() => marketStatus.open && dispatch(openModal('buyModal'))} disabled={!marketStatus.open}
          className={`flex items-center justify-center gap-2 py-3.5 rounded-lg font-syne font-bold ${textSize} transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed ${btnMinH}`}
          style={{ 
            background: buyBtnBg, 
            border: buyBtnBorder,
            color: buyBtnColor 
          }}
          onMouseEnter={e => { e.currentTarget.style.filter = buyBtnHover; }}
          onMouseLeave={e => { e.currentTarget.style.filter = 'none'; }}>
          <i className="fas fa-arrow-trend-up text-xs" /> BUY
        </button>
      </div>

      <div className="flex items-center gap-2 px-4 py-2.5 border-t" style={{ borderColor: 'var(--color-borderMedium)' }}>
        <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: 'var(--color-green)' }} />
        <span className="text-[10px] font-mono font-bold" style={{ color: 'var(--color-textSecondary)' }}>Live Market Data · Lev: {leverageDisplay}</span>
      </div>
    </div>
  );
};

export default OrderPanel;